import styled from "@emotion/styled";
import { PublicKey } from "@solana/web3.js";
import type { StepLiquidityPool } from "@stepfinance/liquidity";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import { Slider } from "antd";
import BigNumber from "bignumber.js";
import type { FC } from "react";
import { useState } from "react";

import { ReactComponent as BackArrowIcon } from "../../assets/back-arrow.svg";
import { ReactComponent as LiquidityEquals } from "../../assets/liquidity-equals.svg";
import { useLanguageContext } from "../../contexts/language/language";
import { useNavigationContext } from "../../contexts/navigation";
import { useLPRatio, useMint } from "../../hooks";
import { isLiquidityPoolUntrackable } from "../../utils/pools/utils";
import { formatPct } from "../../utils/utils";
import { AmountInput } from "../AmountInput/AmountInput";
import { NewRemoveLiquidityButton } from "./NewRemoveLiquidityButton";
import { PoolDataDisplay } from "./PoolDataDisplay";
import { PoolInfoHeader } from "./PoolInfoHeader";
import { RemoveLiquidityPercentageTag as BaseRemoveLiquidityPercentageTag } from "./RemoveLiquidityPercentageTag";

export type RemoveLiquidityProps = {
  pool: StepLiquidityPool | undefined;
  accounts: NormalizedTokenAccount[];
};

export const RemoveLiquidity: FC<RemoveLiquidityProps> = ({ pool, accounts }) => {
  const { navigate } = useNavigationContext();
  const [percentage, setPercentage] = useState<number>(0);
  const ratio = useLPRatio(pool, accounts);
  const filteredAccounts = accounts.filter((account) => account.tokenAmount.uiAmount > 0);

  const {
    currentLanguageData: {
      pages: {
        transactionHistory: {
          transactionTypes: { removeLiquidity },
        },
        swapLP: {
          liquidityTab: { removeLiquidityButton },
        },
      },
    },
  } = useLanguageContext();

  const baseMint = useMint(pool?.mints[0]);
  const quoteMint = useMint(pool?.mints[1]);

  if (!pool) {
    return null;
  }

  const rawPercentage = percentage / 100;
  const liquidityAmountA = new BigNumber(ratio * pool.liquidity[0] * rawPercentage)
    .decimalPlaces(baseMint?.decimals ?? 0)
    .toNumber();
  const liquidityAmountB = new BigNumber(ratio * pool.liquidity[1] * rawPercentage)
    .decimalPlaces(quoteMint?.decimals ?? 0)
    .toNumber();

  const mintA = new PublicKey(pool.mints[0]);
  const mintB = new PublicKey(pool.mints[1]);
  const hasUntrackableValue = isLiquidityPoolUntrackable(pool.liquidityInUSD);
  return (
    <RemoveLiquidityContainer>
      <div className="remove-liquidity-header">
        <BackArrowIcon onClick={() => navigate(`/liquidity/add`)} />
        <RemoveLiquiditySpan>{removeLiquidity}</RemoveLiquiditySpan>
      </div>
      <div className="pool-display">
        <RemovePoolInfoHeader pool={pool} accounts={filteredAccounts} hasUntrackableValue={hasUntrackableValue} />
        <PoolDataDisplay pool={pool} accounts={filteredAccounts} />
        <RemovePercentageHeaderSpan>{removeLiquidityButton}</RemovePercentageHeaderSpan>
        <RemovePercentageSpan>{formatPct.format(percentage / 100)}</RemovePercentageSpan>
        <RemoveSlider value={percentage} step={0.01} tooltipVisible={false} onChange={setPercentage} />
        <div className="remove-liquidity-percentage-buttons">
          <RemoveLiquidityPercentageTag percentage={25} onClick={setPercentage} />
          <RemoveLiquidityPercentageTag percentage={50} onClick={setPercentage} />
          <RemoveLiquidityPercentageTag percentage={75} onClick={setPercentage} />
          <RemoveLiquidityPercentageTag percentage={100} onClick={setPercentage} />
        </div>
      </div>
      <LiquidityEqualsIcon />
      <AmountInput showBalance={false} disabled amount={String(liquidityAmountA)} mint={mintA} />
      <AmountInput
        showBalance={false}
        className="quote-amount-input"
        disabled
        amount={String(liquidityAmountB)}
        mint={mintB}
      />
      <NewRemoveLiquidityButton pool={pool} account={filteredAccounts[0]} removeRatio={rawPercentage} />
    </RemoveLiquidityContainer>
  );
};

const RemoveLiquidityContainer = styled.div`
  background-color: #141414;
  padding: 10px;
  text-align: center;
  border-radius: 0px 8px 8px 8px;

  .remove-liquidity-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin: 10px 0px 14px 0px;
    user-select: none;

    svg {
      cursor: pointer;
    }

    span {
      margin-left: 10px;
      display: inline;
    }
  }

  .pool-display {
    background-color: #202020;
    padding: 40px 30px;
    span {
      display: block;
    }
  }

  .remove-liquidity-percentage-buttons {
    position: relative;
    margin: 0px 6px;
  }

  .quote-amount-input {
    margin-top: 17px;
    margin-bottom: 30px;
  }

  .remove-liquidity-button {
    width: 100%;
    text-align: center;
  }
`;

const RemoveLiquiditySpan = styled.span`
  display: block;
  color: #ffbb1d;
  text-align: initial;
  font-size: 22px;
  font-weight: 700;
  line-height: 22px;
`;

const RemovePoolInfoHeader = styled(PoolInfoHeader)`
  margin-bottom: 30px;
`;

const RemovePercentageHeaderSpan = styled.span`
  color: #b2b2b2;
  text-align: initial;
  margin-top: 30px;
  margin-bottom: 10px;
`;

const RemovePercentageSpan = styled.span`
  text-align: initial;
  font-size: 30px;
  font-weight: 700;
  line-height: 22px;
  margin-bottom: 20px;
`;

const RemoveSlider = styled(Slider)`
  .ant-slider-rail {
    background-color: #004735;
  }

  :hover .ant-slider-rail {
    background-color: #004735;
  }

  .ant-slider-handle:not(.ant-tooltip-open) {
    border-color: #06d6a0;
  }
`;

const RemoveLiquidityPercentageTag = styled(BaseRemoveLiquidityPercentageTag)`
  position: absolute;
  left: ${({ percentage }) => `${percentage}%`};
  transform: ${({ percentage }) => `translate(-${percentage}%)`};
`;

const LiquidityEqualsIcon = styled(LiquidityEquals)`
  text-align: center;
  margin: 30px 0px;
`;
